import { useState, useEffect, useCallback } from 'react';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';
import { addToQueue } from '../utils/syncQueue';
import connectivity from '../utils/connectivity';

const ROLES_ADMIN = ['admin', 'jefe', 'administrativo'];
const MESES    = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DIAS_HDR = ['L','M','X','J','V','S','D'];
const JORNADA_STD = 8;
const TIPO_DOT = {
  nacional: '#4ade80', local: '#38bdf8', empresa: '#f97316',
  jornada7h: '#a78bfa', vacaciones_verano: '#f87171',
  vacaciones_libre: '#f472b6', vacaciones_cierre: '#22d3ee',
};
const COCHE_TIPO_LABELS = {
  mantenimiento: { icon: '🔧', label: 'Mantenimiento' },
  cambio_aceite: { icon: '🛢️', label: 'Cambio de aceite' },
  no_disponible: { icon: '🚫', label: 'No disponible' },
  itv_cita:      { icon: '🛂', label: 'Cita ITV' },
  itv_caducidad: { icon: '⚠️', label: 'Caduca ITV' },
  otro:          { icon: '📌', label: 'Otro' },
};

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function fmtFecha(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('es-ES', { day:'2-digit', month:'short', year:'numeric' });
}
function fmtTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('es-ES', { hour:'2-digit', minute:'2-digit', second:'2-digit' });
}
function fmtHoras(h) {
  if (h == null) return '—';
  const hrs = Math.floor(h), mins = Math.round((h - hrs) * 60);
  return `${hrs}h ${String(mins).padStart(2,'0')}m`;
}
function calcHorasVivas(entrada, salida) {
  if (!entrada) return null;
  return ((salida ? new Date(salida) : new Date()) - new Date(entrada)) / 3600000;
}
function mapsUrl(lat, lng) {
  return `https://www.google.com/maps?q=${parseFloat(lat)},${parseFloat(lng)}`;
}
function initials(nombre) {
  const parts = (nombre || '').trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

const TIPOS_NO_LABORAL = new Set(['nacional','local','empresa','vacaciones_cierre']);
function calcDiasLaborales(ini, fin, festivosArr) {
  if (!ini || !fin || fin < ini) return 0;
  const festivoSet = new Set(
    festivosArr.filter(f => TIPOS_NO_LABORAL.has(f.tipo)).map(f => f.fecha?.split('T')[0]).filter(Boolean)
  );
  let count = 0;
  const cur = new Date(ini + 'T12:00:00');
  const last = new Date(fin + 'T12:00:00');
  while (cur <= last) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6 && !festivoSet.has(cur.toISOString().slice(0, 10))) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

// ── Pending fichaje offline helpers ────────────────────────────────────────
function getPendingFichaje() {
  try { return JSON.parse(localStorage.getItem('sgv_pf') || 'null'); } catch { return null; }
}
function setPendingFichaje(obj) {
  try { localStorage.setItem('sgv_pf', JSON.stringify(obj)); } catch {}
}
function clearPendingFichaje() {
  try { localStorage.removeItem('sgv_pf'); } catch {}
}

export default function Calendario() {
  const user    = useAuthStore(s => s.user);
  const isAdmin = ROLES_ADMIN.includes(user?.rol);

  const today    = new Date();
  const todayStr = isoDate(today);

  // ── Year / month nav ──────────────────────────────────────────────────────
  const [año, setAño] = useState(today.getFullYear());
  const [mes, setMes] = useState(today.getMonth());

  // ── Calendar data ─────────────────────────────────────────────────────────
  const [festivos,      setFestivos]      = useState([]);
  const [vacaciones,    setVacaciones]    = useState([]);
  const [allVacaciones, setAllVacaciones] = useState([]);
  const [allFacturas,   setAllFacturas]   = useState([]);
  const [eventos,       setEventos]       = useState([]);
  const [coches,        setCoches]        = useState([]);
  const [loading,       setLoading]       = useState(true);

  // ── Fichaje data ──────────────────────────────────────────────────────────
  const [hoy,          setHoy]          = useState(null);
  const [monthFich,    setMonthFich]    = useState({});
  const [fichandoIn,   setFichandoIn]   = useState(false);
  const [fichandoOut,  setFichandoOut]  = useState(false);
  const [,             setTick]         = useState(0);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [selectedDay,   setSelectedDay]   = useState(null);
  const [modal,         setModal]         = useState(false);
  const [fechaIni,      setFechaIni]      = useState('');
  const [fechaFin,      setFechaFin]      = useState('');
  const [nota,          setNota]          = useState('');
  const [saving,        setSaving]        = useState(false);
  const [modalF,        setModalF]        = useState(false);
  const [fFecha,        setFFecha]        = useState('');
  const [fNombre,       setFNombre]       = useState('');
  const [fTipo,         setFTipo]         = useState('nacional');
  const [modalE,        setModalE]        = useState(false);
  const [eTitulo,       setETitulo]       = useState('');
  const [eDesc,         setEDesc]         = useState('');
  const [eIni,          setEIni]          = useState('');
  const [eFin,          setEFin]          = useState('');
  const [eColor,        setEColor]        = useState('#fb923c');
  const [festivosOpen,  setFestivosOpen]  = useState(false);
  const [eventosOpen,   setEventosOpen]   = useState(false);

  // ── Loaders ───────────────────────────────────────────────────────────────
  const loadHoy = useCallback(async () => {
    const res = await api.get(`/api/fichajes/hoy?fecha=${todayStr}`).catch(() => ({ data: null }));
    const fromServer = res?.data;
    const pf = getPendingFichaje();
    if (pf && pf.fecha === todayStr) {
      if (fromServer) { clearPendingFichaje(); setHoy(fromServer); }
      else setHoy(pf);
    } else {
      setHoy(fromServer);
    }
  }, [todayStr]);

  const loadMonthFich = useCallback(async () => {
    const mesFmt = `${año}-${String(mes+1).padStart(2,'0')}`;
    const res = await api.get(`/api/fichajes/mine/month?mes=${mesFmt}`).catch(() => ({ data: [] }));
    const map = {};
    (res?.data || []).forEach(f => { map[f.fecha.slice(0,10)] = f; });
    setMonthFich(map);
  }, [año, mes]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fest, vac, evts, coch] = await Promise.all([
        api.get(`/api/calendar/festivos?año=${año}`).catch(() => ({ data: [] })),
        api.get(`/api/calendar/vacaciones?año=${año}`).catch(() => ({ data: [] })),
        api.get(`/api/calendar/eventos?año=${año}`).catch(() => ({ data: [] })),
        api.get('/api/coches').catch(() => ({ data: [] })),
      ]);
      setFestivos(fest.data || []);
      setVacaciones(vac.data || []);
      setEventos(evts.data || []);
      setCoches(coch.data || []);
      if (isAdmin) {
        const [all, fact] = await Promise.all([
          api.get('/api/calendar/vacaciones/all').catch(() => ({ data: [] })),
          api.get('/api/facturas/all').catch(() => ({ data: [] })),
        ]);
        setAllVacaciones(all.data || []);
        setAllFacturas(fact.data || []);
      }
    } catch {}
    setLoading(false);
  }, [año, isAdmin]);

  useEffect(() => { load(); },                  [load]);
  useEffect(() => { loadHoy(); },               [loadHoy]);
  useEffect(() => { loadMonthFich(); },         [loadMonthFich]);

  // Al entrar como técnico, marcar como vistas las vacaciones/bajas ya
  // resueltas (quita el aviso "1" del sidebar).
  useEffect(() => {
    if (isAdmin) return;
    api.post('/api/calendar/vacaciones/marcar-vistas')
      .then(() => window.dispatchEvent(new CustomEvent('sgv:pendientes')))
      .catch(() => {});
  }, [isAdmin]);

  // Live timer for hoy entry
  useEffect(() => {
    if (!hoy?.entrada || hoy?.salida) return;
    const id = setInterval(() => setTick(t => t+1), 1000);
    return () => clearInterval(id);
  }, [hoy]);

  // Reload on sync
  useEffect(() => {
    const handler = () => { clearPendingFichaje(); loadHoy(); loadMonthFich(); };
    window.addEventListener('sgv:synced', handler);
    return () => window.removeEventListener('sgv:synced', handler);
  }, [loadHoy, loadMonthFich]);

  // ── Fichar ────────────────────────────────────────────────────────────────
  async function getPos() {
    return new Promise(resolve => {
      if (!navigator.geolocation) { resolve({}); return; }
      navigator.geolocation.getCurrentPosition(
        p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve({}), { timeout: 8000 }
      );
    });
  }

  function applyPendingFichaje(tipo) {
    const ts = new Date().toISOString();
    // Si la entrada se hizo online no hay nada en sgv_pf; usamos hoy (del server) como base
    const prev = getPendingFichaje() || hoy || {};
    const updated = tipo === 'entrada'
      ? { fecha: todayStr, entrada: ts, salida: null, _pending: true }
      : { ...prev, fecha: todayStr, salida: ts, _pending: true };
    setPendingFichaje(updated);
    setHoy(updated);
  }

  async function fichar(tipo) {
    if (tipo === 'entrada') setFichandoIn(true); else setFichandoOut(true);
    try {
      const pos = await getPos();
      const payload = { ...pos, fecha: todayStr };
      const endpoint = `/api/fichajes/${tipo}`;

      if (!navigator.onLine || !connectivity.isOnline) {
        addToQueue('POST', endpoint, JSON.stringify(payload));
        applyPendingFichaje(tipo);
        return;
      }

      let res;
      try {
        res = await api.post(endpoint, payload);
      } catch {
        if (!navigator.onLine || !connectivity.isOnline) {
          addToQueue('POST', endpoint, JSON.stringify(payload));
          applyPendingFichaje(tipo);
        } else {
          alert('Error al registrar el fichaje. Inténtalo de nuevo.');
        }
        return;
      }

      if (res?.queued) {
        applyPendingFichaje(tipo);
      } else {
        clearPendingFichaje();
        await Promise.all([loadHoy(), loadMonthFich()]);
      }
    } finally {
      setFichandoIn(false); setFichandoOut(false);
    }
  }

  // ── Calendar actions ──────────────────────────────────────────────────────
  async function solicitarVac() {
    if (!fechaIni || !fechaFin || fechaFin < fechaIni) return;
    setSaving(true);
    try {
      await api.post('/api/calendar/vacaciones', { fecha_inicio: fechaIni, fecha_fin: fechaFin, nota });
      setModal(false); setFechaIni(''); setFechaFin(''); setNota('');
      await load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function responderVac(id, estado) {
    try { await api.put(`/api/calendar/vacaciones/${id}`, { estado }); await load(); window.dispatchEvent(new CustomEvent('sgv:pendientes')); }
    catch (e) { alert(e.message); }
  }

  async function cancelarVac(id) {
    if (!window.confirm('¿Cancelar solicitud?')) return;
    try { await api.delete(`/api/calendar/vacaciones/${id}`); await load(); }
    catch (e) { alert(e.message); }
  }

  async function responderFactura(id, estado) {
    try {
      await api.put(`/api/facturas/${id}`, { estado });
      setAllFacturas(prev => prev.map(f => f.id === id ? { ...f, estado } : f));
      window.dispatchEvent(new CustomEvent('sgv:pendientes'));
    } catch (e) { alert(e.message); }
  }

  async function viewFacturaFile(f) {
    try {
      const blob = await api.get(`/api/facturas/${f.id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { alert('Foto no disponible'); }
  }

  async function addFestivo() {
    if (!fFecha || !fNombre) return;
    setSaving(true);
    try {
      await api.post('/api/calendar/festivos', { fecha: fFecha, nombre: fNombre, tipo: fTipo });
      setModalF(false); setFFecha(''); setFNombre(''); setFTipo('nacional');
      await load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function addEvento() {
    if (!eTitulo || !eIni || !eFin) return;
    setSaving(true);
    try {
      await api.post('/api/calendar/eventos', { titulo: eTitulo, descripcion: eDesc, fecha_inicio: eIni, fecha_fin: eFin, color: eColor });
      setModalE(false); setETitulo(''); setEDesc(''); setEIni(''); setEFin(''); setEColor('#fb923c');
      await load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function delEvento(id) {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try { await api.delete(`/api/calendar/eventos/${id}`); await load(); }
    catch (e) { alert(e.message); }
  }

  async function delFestivo(id) {
    if (!window.confirm('¿Eliminar festivo?')) return;
    try { await api.delete(`/api/calendar/festivos/${id}`); await load(); }
    catch (e) { alert(e.message); }
  }

  function prevMes() { if (mes === 0) { setMes(11); setAño(a => a-1); } else setMes(m => m-1); setSelectedDay(null); }
  function nextMes() { if (mes === 11) { setMes(0); setAño(a => a+1); } else setMes(m => m+1); setSelectedDay(null); }

  // ── Build calendar grid ───────────────────────────────────────────────────
  function buildDays() {
    const first = new Date(año, mes, 1), last = new Date(año, mes+1, 0);
    const startDow = (first.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(new Date(año, mes, d));
    return days;
  }

  const festivosByDate = {};
  festivos.forEach(f => {
    const d = f.fecha?.split('T')[0]; if (!d) return;
    if (!festivosByDate[d]) festivosByDate[d] = [];
    festivosByDate[d].push(f);
  });

  const eventosByDate = {};
  eventos.forEach(ev => {
    if (!ev.fecha_inicio) return;
    const d1 = new Date(ev.fecha_inicio+'T12:00:00'), d2 = new Date(ev.fecha_fin+'T12:00:00');
    for (const d = new Date(d1); d <= d2; d.setDate(d.getDate()+1)) {
      const ds = isoDate(new Date(d));
      if (!eventosByDate[ds]) eventosByDate[ds] = [];
      eventosByDate[ds].push(ev);
    }
  });

  const jornada7hSet = new Set(
    festivos.filter(f => f.tipo === 'jornada7h').map(f => f.fecha?.split('T')[0]).filter(Boolean)
  );
  const jornadaStd = dateStr => jornada7hSet.has(dateStr) ? 7 : JORNADA_STD;

  const cochesByDate = {};
  function pushCocheEvento(ds, entry) {
    if (!cochesByDate[ds]) cochesByDate[ds] = [];
    cochesByDate[ds].push(entry);
  }
  coches.forEach(c => {
    (c.eventos || []).forEach(ev => {
      if (!ev.fecha_inicio) return;
      const d1 = new Date(ev.fecha_inicio + 'T12:00:00');
      const d2 = ev.fecha_fin ? new Date(ev.fecha_fin + 'T12:00:00') : d1;
      for (const d = new Date(d1); d <= d2; d.setDate(d.getDate() + 1)) {
        pushCocheEvento(isoDate(new Date(d)), { coche: c.nombre, tipo: ev.tipo, descripcion: ev.descripcion });
      }
    });
    if (c.itv_caducidad) {
      pushCocheEvento(c.itv_caducidad.slice(0, 10), { coche: c.nombre, tipo: 'itv_caducidad', descripcion: null });
    }
  });

  function buildMyVacMap() {
    const map = {};
    vacaciones.forEach(v => {
      if (!v.fecha_inicio) return;
      const d1 = new Date(v.fecha_inicio), d2 = new Date(v.fecha_fin);
      for (const d = new Date(d1); d <= d2; d.setDate(d.getDate()+1))
        map[isoDate(new Date(d))] = v.estado;
    });
    return map;
  }

  // Vacaciones de todos los empleados por día, para mostrar iniciales en la celda
  // (admin/jefe/administrativo). Rechazadas no se muestran, no ocupan el día.
  const adminVacByDate = {};
  if (isAdmin) {
    allVacaciones.forEach(v => {
      if (!v.fecha_inicio || v.estado === 'rechazada') return;
      const d1 = new Date(v.fecha_inicio), d2 = new Date(v.fecha_fin);
      for (const d = new Date(d1); d <= d2; d.setDate(d.getDate()+1)) {
        const ds = isoDate(new Date(d));
        if (!adminVacByDate[ds]) adminVacByDate[ds] = [];
        adminVacByDate[ds].push(v);
      }
    });
  }

  const days      = buildDays();
  const myVacMap  = buildMyVacMap();
  const diasAprobados  = vacaciones.filter(v => v.estado === 'aprobada').reduce((s,v) => s + (v.dias||0), 0);
  const diasPendientes = vacaciones.filter(v => v.estado === 'pendiente').reduce((s,v) => s + (v.dias||0), 0);
  const pendientes     = allVacaciones.filter(v => v.estado === 'pendiente');
  const horasVivas     = hoy ? calcHorasVivas(hoy.entrada, hoy.salida) : null;
  const stdHoy          = jornadaStd(todayStr);
  const extras         = horasVivas != null && horasVivas > stdHoy ? horasVivas - stdHoy : null;

  if (loading) return <div style={{ padding: 32, color: 'var(--text-dim)', textAlign: 'center' }}>Cargando…</div>;

  return (
    <div style={{ padding: 'clamp(12px,3vw,24px)', maxWidth: 1000, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>Calendario</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setModal(true)} style={{ background: 'var(--amber)', color: '#000', border: 'none', padding: '9px 16px', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 13 }}>
            + Solicitar Vacaciones
          </button>
        </div>
      </div>

      {/* ── FICHAJE HOY ──────────────────────────────────────────────────── */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' }}>
            Fichaje — {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long' })}
          </div>
          {hoy?._pending && (
            <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 4, background: 'rgba(245,158,11,.12)', color: 'var(--amber)', border: '1px solid rgba(245,158,11,.3)' }}>
              ⏳ Pendiente de sincronizar
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 28 }}>
            <TimeBox label="Entrada" val={fmtTime(hoy?.entrada)} color={hoy?.entrada ? '#4ade80' : 'var(--text-dim)'} />
            <TimeBox label="Salida"  val={fmtTime(hoy?.salida)}  color={hoy?.salida  ? '#f87171' : 'var(--text-dim)'} />
            {horasVivas != null && <TimeBox label="Horas" val={fmtHoras(horasVivas)} color={extras != null ? '#f87171' : 'var(--amber)'} extras={extras} />}
          </div>
          <div style={{ display: 'flex', gap: 10, marginLeft: 'auto', flexWrap: 'wrap' }}>
            {!hoy?.entrada && (
              <button onClick={() => fichar('entrada')} disabled={fichandoIn} style={BTN_IN}>
                {fichandoIn ? '📍 Localizando…' : '▶ Fichar Entrada'}
              </button>
            )}
            {hoy?.entrada && !hoy?.salida && (
              <button onClick={() => fichar('salida')} disabled={fichandoOut} style={BTN_OUT}>
                {fichandoOut ? '📍 Localizando…' : '■ Fichar Salida'}
              </button>
            )}
            {hoy?.entrada && hoy?.salida && (
              <div style={{ fontSize: 13, color: '#4ade80', fontWeight: 800 }}>✓ Jornada completada</div>
            )}
          </div>
        </div>
        {(hoy?.lat_entrada || hoy?.lat_salida) && (
          <div style={{ marginTop: 10, fontSize: 10, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {hoy.lat_entrada && <a href={mapsUrl(hoy.lat_entrada, hoy.lng_entrada)} target="_blank" rel="noreferrer" style={LOC_LINK}>📍 Entrada: {parseFloat(hoy.lat_entrada).toFixed(5)}, {parseFloat(hoy.lng_entrada).toFixed(5)}</a>}
            {hoy.lat_salida  && <a href={mapsUrl(hoy.lat_salida,  hoy.lng_salida)}  target="_blank" rel="noreferrer" style={LOC_LINK}>📍 Salida: {parseFloat(hoy.lat_salida).toFixed(5)}, {parseFloat(hoy.lng_salida).toFixed(5)}</a>}
          </div>
        )}
      </div>

      {/* ── CALENDARIO MES ───────────────────────────────────────────────── */}
      <MonthCalendarCard
        año={año} mes={mes} prevMes={prevMes} nextMes={nextMes} days={days} todayStr={todayStr}
        myVacMap={myVacMap} festivosByDate={festivosByDate} eventosByDate={eventosByDate}
        cochesByDate={cochesByDate} adminVacByDate={adminVacByDate}
        isAdmin={isAdmin} hoy={hoy} monthFich={monthFich} vacaciones={vacaciones} allVacaciones={allVacaciones}
        selectedDay={selectedDay} setSelectedDay={setSelectedDay} jornadaStd={jornadaStd}
      />

      {/* ── CONTADORES VACACIONES (debajo del calendario) ────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 10, marginBottom: 14 }}>
        <StatBox label="Disponibles" val={22 - diasAprobados} color="var(--amber)" />
        <StatBox label="Aprobados"   val={diasAprobados}      color="#4ade80" />
        <StatBox label="Pendientes"  val={diasPendientes}     color="#60a5fa" />
        <StatBox label="Total anual" val={22}                 color="var(--text-dim)" />
      </div>

      {/* ── MIS SOLICITUDES ───────────────────────────────────────────────── */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
        <div style={STITLE}>Mis solicitudes de vacaciones</div>
        {vacaciones.length === 0
          ? <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>Sin solicitudes. Usa el botón "Solicitar Vacaciones" para pedir días.</div>
          : <div style={{ display: 'grid', gap: 6 }}>
              {vacaciones.map(v => (
                <div key={v.id} style={ROW}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                      {fmtFecha(v.fecha_inicio)} → {fmtFecha(v.fecha_fin)}
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-dim)' }}>{v.dias} día{v.dias !== 1 ? 's' : ''}</span>
                    </div>
                    {v.nota && <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{v.nota}</div>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <EstadoBadge estado={v.estado} tipo={v.tipo} />
                    {v.estado === 'pendiente' && <button onClick={() => cancelarVac(v.id)} style={BTN_DEL}>Cancelar</button>}
                  </div>
                </div>
              ))}
            </div>
        }
      </div>

      {/* ── ADMIN: solicitudes pendientes ─────────────────────────────────── */}
      {isAdmin && pendientes.length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1.5px solid rgba(96,165,250,.3)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ ...STITLE, color: '#60a5fa' }}>⏳ Solicitudes pendientes ({pendientes.length})</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {pendientes.map(v => (
              <div key={v.id} style={ROW}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{v.user_nombre}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>{fmtFecha(v.fecha_inicio)} → {fmtFecha(v.fecha_fin)} · {v.dias} día{v.dias !== 1 ? 's' : ''}</div>
                  {v.nota && <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{v.nota}</div>}
                </div>
                <div style={{ display: 'flex', gap: 7 }}>
                  <button onClick={() => responderVac(v.id, 'aprobada')}  style={BTN_OK}>✓ Aprobar</button>
                  <button onClick={() => responderVac(v.id, 'rechazada')} style={BTN_DEL}>✗ Rechazar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ADMIN: facturas pendientes ────────────────────────────────────── */}
      {isAdmin && allFacturas.filter(f => f.estado === 'pendiente').length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1.5px solid rgba(74,222,128,.3)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ ...STITLE, color: '#4ade80', marginBottom: 12 }}>
            💳 Reembolsos pendientes ({allFacturas.filter(f => f.estado === 'pendiente').length})
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {allFacturas.filter(f => f.estado === 'pendiente').map(f => (
              <div key={f.id} style={ROW}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{f.user_nombre}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.descripcion}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 1 }}>{new Date(f.fecha).toLocaleDateString('es-ES', { day:'2-digit', month:'short', year:'numeric' })}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--amber)', whiteSpace: 'nowrap' }}>{parseFloat(f.importe).toFixed(2)} €</div>
                  {f.has_file && <button onClick={() => viewFacturaFile(f)} style={{ background: 'rgba(96,165,250,.1)', border: '1px solid rgba(96,165,250,.3)', color: '#60a5fa', padding: '5px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>🖼</button>}
                  <button onClick={() => responderFactura(f.id, 'aprobada')}  style={{ background: 'rgba(74,222,128,.12)', border: '1px solid rgba(74,222,128,.4)', color: '#4ade80', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>✓ Aprobar</button>
                  <button onClick={() => responderFactura(f.id, 'rechazada')} style={{ background: 'rgba(248,113,113,.12)', border: '1px solid rgba(248,113,113,.4)', color: '#f87171', padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>✗ Rechazar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* ── ADMIN: festivos ───────────────────────────────────────────────── */}
      {isAdmin && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button onClick={() => setFestivosOpen(o => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: 0 }}>
              <span style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' }}>Festivos {año} ({festivos.length})</span>
              <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>{festivosOpen ? '▲' : '▼'}</span>
            </button>
            <button onClick={() => setModalF(true)} style={{ background: 'transparent', border: '1px solid var(--amber)', color: 'var(--amber)', padding: '5px 12px', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>+ Añadir</button>
          </div>
          {festivosOpen && (
            <div style={{ marginTop: 12 }}>
              {festivos.length === 0
                ? <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>Sin festivos para {año}.</div>
                : <div style={{ display: 'grid', gap: 4 }}>
                    {festivos.map(f => (
                      <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Dot color={TIPO_DOT[f.tipo] || '#7888a8'} />
                          <span style={{ color: 'var(--text-dim)', fontWeight: 700, minWidth: 80 }}>{new Date(f.fecha).toLocaleDateString('es-ES', { day:'2-digit', month:'short' })}</span>
                          <span style={{ color: 'var(--text)' }}>{f.nombre}</span>
                          <span style={{ color: 'var(--text-dim)', fontSize: 10 }}>{f.tipo}</span>
                        </div>
                        <button onClick={() => delFestivo(f.id)} style={{ background: 'none', border: 'none', color: '#4e5a7a', cursor: 'pointer', fontSize: 13, padding: '2px 6px' }}>🗑</button>
                      </div>
                    ))}
                  </div>
              }
            </div>
          )}
        </div>
      )}

      {/* ── ADMIN: eventos ────────────────────────────────────────────────── */}
      {isAdmin && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button onClick={() => setEventosOpen(o => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: 0 }}>
              <span style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' }}>Eventos empresa {año} ({eventos.length})</span>
              <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>{eventosOpen ? '▲' : '▼'}</span>
            </button>
            <button onClick={() => setModalE(true)} style={{ background: 'transparent', border: '1px solid var(--amber)', color: 'var(--amber)', padding: '5px 12px', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>+ Añadir</button>
          </div>
          {eventosOpen && (
            <div style={{ marginTop: 12 }}>
              {eventos.length === 0
                ? <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>Sin eventos para {año}.</div>
                : <div style={{ display: 'grid', gap: 4 }}>
                    {eventos.map(ev => (
                      <div key={ev.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          <DotSquare color={ev.color} />
                          <div>
                            <span style={{ color: 'var(--text)', fontWeight: 700 }}>{ev.titulo}</span>
                            <span style={{ color: 'var(--text-dim)', fontSize: 10, marginLeft: 8 }}>
                              {fmtFecha(ev.fecha_inicio)}{ev.fecha_inicio !== ev.fecha_fin ? ` → ${fmtFecha(ev.fecha_fin)}` : ''}
                            </span>
                            {ev.descripcion && <div style={{ color: 'var(--text-dim)', fontSize: 11 }}>{ev.descripcion}</div>}
                          </div>
                        </div>
                        <button onClick={() => delEvento(ev.id)} style={{ background: 'none', border: 'none', color: '#4e5a7a', cursor: 'pointer', fontSize: 13, padding: '2px 6px', flexShrink: 0 }}>🗑</button>
                      </div>
                    ))}
                  </div>
              }
            </div>
          )}
        </div>
      )}

      {/* ── MODALS ────────────────────────────────────────────────────────── */}
      {modal && (
        <Overlay onClose={() => setModal(false)}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--amber)', marginBottom: 16 }}>Solicitar Vacaciones</div>
          <Fld label="Fecha inicio"><input type="date" value={fechaIni} onChange={e => setFechaIni(e.target.value)} style={INP} /></Fld>
          <Fld label="Fecha fin"><input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} min={fechaIni} style={INP} /></Fld>
          {fechaIni && fechaFin && fechaFin >= fechaIni && (() => {
            const laborales = calcDiasLaborales(fechaIni, fechaFin, festivos);
            const total = Math.round((new Date(fechaFin) - new Date(fechaIni)) / 86400000) + 1;
            return (
              <div style={{ fontSize: 12, color: '#60a5fa', marginBottom: 10, fontWeight: 700 }}>
                {laborales} día{laborales !== 1 ? 's' : ''} laborables
                {total !== laborales && <span style={{ fontWeight: 400, color: 'var(--text-dim)', marginLeft: 6 }}>({total} naturales, {total - laborales} excluidos)</span>}
              </div>
            );
          })()}
          <Fld label="Nota (opcional)"><input type="text" value={nota} onChange={e => setNota(e.target.value)} placeholder="Motivo o comentario" style={INP} /></Fld>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={solicitarVac} disabled={saving || !fechaIni || !fechaFin || fechaFin < fechaIni}
              style={{ flex: 1, background: 'var(--amber)', color: '#000', border: 'none', padding: '9px 0', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 13, opacity: (!fechaIni || !fechaFin || fechaFin < fechaIni) ? .5 : 1 }}>
              {saving ? 'Enviando…' : 'Enviar solicitud'}
            </button>
            <button onClick={() => setModal(false)} style={BTN_SEC}>Cancelar</button>
          </div>
        </Overlay>
      )}

      {modalF && (
        <Overlay onClose={() => setModalF(false)}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--amber)', marginBottom: 16 }}>Añadir Festivo</div>
          <Fld label="Fecha"><input type="date" value={fFecha} onChange={e => setFFecha(e.target.value)} style={INP} /></Fld>
          <Fld label="Nombre"><input type="text" value={fNombre} onChange={e => setFNombre(e.target.value)} placeholder="Ej: Día de la Constitución" style={INP} /></Fld>
          <Fld label="Tipo">
            <select value={fTipo} onChange={e => setFTipo(e.target.value)} style={INP}>
              <option value="nacional">Nacional</option>
              <option value="local">Local</option>
              <option value="empresa">Empresa / Puente</option>
              <option value="jornada7h">Jornada 7h</option>
              <option value="vacaciones_verano">Vacaciones verano</option>
              <option value="vacaciones_libre">Vacaciones libre disposición</option>
              <option value="vacaciones_cierre">Cierre empresa</option>
            </select>
          </Fld>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={addFestivo} disabled={saving || !fFecha || !fNombre}
              style={{ flex: 1, background: 'var(--amber)', color: '#000', border: 'none', padding: '9px 0', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 13 }}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            <button onClick={() => setModalF(false)} style={BTN_SEC}>Cancelar</button>
          </div>
        </Overlay>
      )}

      {modalE && (
        <Overlay onClose={() => setModalE(false)}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--amber)', marginBottom: 16 }}>Añadir Evento de Empresa</div>
          <Fld label="Título *"><input type="text" value={eTitulo} onChange={e => setETitulo(e.target.value)} placeholder="Ej: Revisión ITV furgoneta" style={INP} /></Fld>
          <Fld label="Descripción (opcional)"><input type="text" value={eDesc} onChange={e => setEDesc(e.target.value)} placeholder="Detalles adicionales…" style={INP} /></Fld>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Fld label="Fecha inicio *"><input type="date" value={eIni} onChange={e => setEIni(e.target.value)} style={INP} /></Fld>
            <Fld label="Fecha fin *"><input type="date" value={eFin} onChange={e => setEFin(e.target.value)} min={eIni} style={INP} /></Fld>
          </div>
          <Fld label="Color">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[['#fb923c','Naranja'],['#c084fc','Morado'],['#34d399','Verde'],['#60a5fa','Azul'],['#f472b6','Rosa'],['#fbbf24','Amarillo'],['#f87171','Rojo']].map(([c,label]) => (
                <button key={c} title={label} onClick={() => setEColor(c)}
                  style={{ width: 26, height: 26, borderRadius: '50%', background: c, border: eColor === c ? '3px solid var(--text)' : '2px solid transparent', cursor: 'pointer', padding: 0 }} />
              ))}
            </div>
          </Fld>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={addEvento} disabled={saving || !eTitulo || !eIni || !eFin || eFin < eIni}
              style={{ flex: 1, background: 'var(--amber)', color: '#000', border: 'none', padding: '9px 0', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 13, opacity: (!eTitulo || !eIni || !eFin || eFin < eIni) ? .5 : 1 }}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            <button onClick={() => setModalE(false)} style={BTN_SEC}>Cancelar</button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function TimeBox({ label, val, color, extras }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 900, color }}>{val}</div>
      {extras != null && <div style={{ fontSize: 10, fontWeight: 800, color: '#f87171', marginTop: 2 }}>
        +{Math.floor(extras)}h {String(Math.round((extras % 1)*60)).padStart(2,'0')}m extra
      </div>}
    </div>
  );
}

function MonthCalendarCard({
  año, mes, prevMes, nextMes, days, todayStr,
  myVacMap, festivosByDate, eventosByDate, cochesByDate, adminVacByDate,
  isAdmin, hoy, monthFich, vacaciones, allVacaciones,
  selectedDay, setSelectedDay, jornadaStd,
}) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px', marginBottom: 14 }}>
      {/* Nav mes */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <button onClick={prevMes} style={BTN_NAV}>‹</button>
        <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text)' }}>{MESES[mes]} {año}</div>
        <button onClick={nextMes} style={BTN_NAV}>›</button>
      </div>

      {/* Cabeceras días */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3, marginBottom: 4 }}>
        {DIAS_HDR.map((d, i) => (
          <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: i >= 5 ? '#f87171' : 'var(--text-dim)', padding: '3px 0' }}>{d}</div>
        ))}
      </div>

      {/* Celdas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3 }}>
        {days.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const ds       = isoDate(day);
          const isToday  = ds === todayStr;
          const dow      = (day.getDay() + 6) % 7;
          const isWknd   = dow >= 5;
          const myVac    = myVacMap[ds];
          const dayFests = festivosByDate[ds] || [];
          const dayEvts  = eventosByDate[ds]  || [];
          const dayCoches = cochesByDate[ds]  || [];
          const dayVacs   = adminVacByDate[ds] || [];
          const tipos    = new Set(dayFests.map(f => f.tipo));
          const dayFich  = ds === todayStr ? hoy : monthFich[ds];
          const cellBg   = myVac === 'aprobada'       ? 'rgba(74,222,128,.12)'
            : myVac === 'pendiente'                    ? 'rgba(96,165,250,.1)'
            : isToday                                  ? 'rgba(245,158,11,.1)'
            : tipos.has('vacaciones_cierre')           ? 'rgba(34,211,238,.07)'
            : tipos.has('vacaciones_verano')           ? 'rgba(248,113,113,.07)'
            : tipos.has('vacaciones_libre')            ? 'rgba(244,114,182,.07)'
            : tipos.has('jornada7h')                   ? 'rgba(167,139,250,.07)'
            : isWknd                                   ? 'rgba(248,113,113,.04)'
            : 'transparent';
          const cellBorder = myVac === 'aprobada'  ? '1.5px solid rgba(74,222,128,.5)'
            : myVac === 'pendiente'                 ? '1.5px solid rgba(96,165,250,.4)'
            : isToday                              ? '1.5px solid var(--amber)'
            : '1px solid var(--border)';
          const isSelected = selectedDay === ds;
          return (
            <div key={ds}
              onClick={() => setSelectedDay(isSelected ? null : ds)}
              style={{
                borderRadius: 6, padding: '5px 3px', textAlign: 'center', minHeight: 40,
                background: isSelected ? 'rgba(245,158,11,.15)' : cellBg,
                border: isSelected ? '1.5px solid var(--amber)' : cellBorder,
                cursor: 'pointer', transition: 'all .1s',
              }}>
              <div style={{ fontSize: 12, fontWeight: isToday ? 900 : 600, color: isWknd ? '#f87171' : 'var(--text)' }}>{day.getDate()}</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 2, marginTop: 3, flexWrap: 'wrap' }}>
                {dayFich && <Dot color={dayFich.salida ? '#4ade80' : '#f59e0b'} />}
                {[...tipos].map(t => TIPO_DOT[t] && <Dot key={t} color={TIPO_DOT[t]} />)}
                {dayEvts.map((ev, j) => <DotSquare key={`ev${j}`} color={ev.color} />)}
                {dayCoches.length > 0 && <DotSquare color="#38bdf8" />}
                {dayVacs.slice(0, 3).map(v => (
                  <VacInitials key={v.id} label={initials(v.user_nombre)} estado={v.estado} tipo={v.tipo}
                    title={`${v.user_nombre} · ${v.tipo === 'baja' ? 'baja' : v.estado}`} />
                ))}
                {dayVacs.length > 3 && (
                  <span style={{ fontSize: 7, fontWeight: 800, color: 'var(--text-dim)' }}>+{dayVacs.length - 3}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Leyenda */}
      <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
          <Dot color="#4ade80" /> Fichaje completo
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
          <Dot color="#f59e0b" /> Sin salida
        </div>
        {[['#4ade80','Nacional'],['#38bdf8','Local'],['#f97316','Empresa'],['#a78bfa','Jornada 7h'],['#f87171','Vac. verano'],['#f472b6','Vac. libre'],['#22d3ee','Cierre']].map(([c,l]) => (
          <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
            <Dot color={c} /> {l}
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: 'rgba(74,222,128,.12)', border: '1.5px solid rgba(74,222,128,.5)', display: 'inline-block' }} /> Vac. aprobadas
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
          <DotSquare color="#fb923c" /> Evento empresa
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
          <DotSquare color="#38bdf8" /> 🚗 Vehículo
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
            <VacInitials label="AC" estado="aprobada" /> Vacaciones (verde = confirmada, azul = pendiente)
          </div>
        )}
        {isAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
            <VacInitials label="AC" tipo="baja" /> Baja
          </div>
        )}
      </div>

      {/* Panel día seleccionado */}
      {selectedDay && (() => {
        const dayFests  = festivosByDate[selectedDay] || [];
        const dayEvts   = eventosByDate[selectedDay]  || [];
        const dayCoches = cochesByDate[selectedDay]   || [];
        const myVacObj  = vacaciones.find(v => {
          const d1 = v.fecha_inicio?.split('T')[0], d2 = v.fecha_fin?.split('T')[0];
          return d1 && selectedDay >= d1 && selectedDay <= d2;
        });
        const adminVacs = isAdmin ? allVacaciones.filter(v => {
          const d1 = v.fecha_inicio?.split('T')[0], d2 = v.fecha_fin?.split('T')[0];
          return d1 && selectedDay >= d1 && selectedDay <= d2;
        }) : [];
        const dayDate = new Date(selectedDay + 'T12:00:00');
        const dow     = (dayDate.getDay() + 6) % 7;
        const DIAS    = ['Lunes','Martes','Miércoles','Jueves','Viernes','Sábado','Domingo'];
        const dayFich = selectedDay === todayStr ? hoy : monthFich[selectedDay];
        const hv      = dayFich ? calcHorasVivas(dayFich.entrada, dayFich.salida) : null;

        return (
          <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)' }}>
                {DIAS[dow]}, {dayDate.toLocaleDateString('es-ES', { day:'2-digit', month:'long', year:'numeric' })}
              </div>
              <button onClick={() => setSelectedDay(null)} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 16 }}>✕</button>
            </div>

            {/* Fichaje del día */}
            {dayFich ? (
              <div style={{ background: 'var(--surface-deep)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                <div style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>
                  Fichaje {dayFich._pending ? '⏳' : ''}
                </div>
                <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
                  <TimeBox label="Entrada" val={fmtTime(dayFich.entrada)} color={dayFich.entrada ? '#4ade80' : 'var(--text-dim)'} />
                  <TimeBox label="Salida"  val={fmtTime(dayFich.salida)}  color={dayFich.salida  ? '#f87171' : 'var(--text-dim)'} />
                  {hv != null && <TimeBox label="Total" val={fmtHoras(hv)} color={hv > jornadaStd(selectedDay) ? '#f87171' : 'var(--amber)'} extras={hv > jornadaStd(selectedDay) ? hv - jornadaStd(selectedDay) : null} />}
                  {!dayFich.salida && dayFich.entrada && <TimeBox label="Estado" val="En curso" color="#60a5fa" />}
                </div>
                {(dayFich.lat_entrada || dayFich.lat_salida) && (
                  <div style={{ marginTop: 8, fontSize: 10, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {dayFich.lat_entrada && <a href={mapsUrl(dayFich.lat_entrada, dayFich.lng_entrada)} target="_blank" rel="noreferrer" style={LOC_LINK}>📍 Entrada</a>}
                    {dayFich.lat_salida  && <a href={mapsUrl(dayFich.lat_salida,  dayFich.lng_salida)}  target="_blank" rel="noreferrer" style={LOC_LINK}>📍 Salida</a>}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 8 }}>Sin fichaje registrado este día.</div>
            )}

            {dayEvts.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                {dayEvts.map(ev => (
                  <div key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 0', fontSize: 12 }}>
                    <DotSquare color={ev.color} />
                    <span style={{ color: 'var(--text)', fontWeight: 700 }}>{ev.titulo}</span>
                    {ev.descripcion && <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>{ev.descripcion}</span>}
                  </div>
                ))}
              </div>
            )}

            {dayFests.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                {dayFests.map(f => (
                  <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 0', fontSize: 12 }}>
                    <Dot color={TIPO_DOT[f.tipo] || '#7888a8'} />
                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>{f.nombre}</span>
                    <span style={{ color: 'var(--text-dim)', fontSize: 10 }}>({f.tipo})</span>
                  </div>
                ))}
              </div>
            )}

            {dayCoches.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                {dayCoches.map((c, i) => {
                  const tc = COCHE_TIPO_LABELS[c.tipo] || COCHE_TIPO_LABELS.otro;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 0', fontSize: 12 }}>
                      <span>{tc.icon}</span>
                      <span style={{ color: 'var(--text)', fontWeight: 700 }}>🚗 {c.coche}</span>
                      <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>{tc.label}</span>
                      {c.descripcion && <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>· {c.descripcion}</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {myVacObj && (
              <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ color: 'var(--text-dim)' }}>Mis vacaciones:</span>
                <EstadoBadge estado={myVacObj.estado} tipo={myVacObj.tipo} />
                <span style={{ color: 'var(--text-dim)' }}>({fmtFecha(myVacObj.fecha_inicio)} → {fmtFecha(myVacObj.fecha_fin)})</span>
              </div>
            )}

            {isAdmin && adminVacs.length > 0 && (
              <div style={{ marginTop: 6 }}>
                <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 }}>Vacaciones de empleados</div>
                {adminVacs.map(v => (
                  <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '3px 0' }}>
                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>{v.user_nombre}</span>
                    <EstadoBadge estado={v.estado} tipo={v.tipo} />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

function FichajeMonthGrid({ rows, año, mes, festivos, onEdit }) {
  const [sel, setSel] = useState(null);

  const daysInMonth = new Date(año, mes + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const festivoSet = new Set(
    (festivos || []).filter(f => TIPOS_NO_LABORAL.has(f.tipo)).map(f => f.fecha?.slice(0, 10)).filter(Boolean)
  );
  const jornada7hSet = new Set(
    (festivos || []).filter(f => f.tipo === 'jornada7h').map(f => f.fecha?.slice(0, 10)).filter(Boolean)
  );

  const byEmployee = {};
  rows.forEach(f => {
    const name = f.user_nombre;
    if (!byEmployee[name]) byEmployee[name] = {};
    const day = parseInt(f.fecha.slice(8, 10), 10);
    byEmployee[name][day] = f;
  });
  const employees = Object.keys(byEmployee).sort();

  return (
    <div style={{ overflowX: 'auto', marginTop: 4 }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
        <thead>
          <tr>
            <th style={{ ...TH, textAlign: 'left', minWidth: 110, position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 1 }}>Empleado</th>
            {days.map(d => {
              const dow = new Date(año, mes, d).getDay();
              const isWeekend = dow === 0 || dow === 6;
              const iso = `${año}-${String(mes+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
              const isFestivo = festivoSet.has(iso);
              return (
                <th key={d} style={{ ...TH, width: 28, color: isWeekend || isFestivo ? '#4e5a7a' : 'var(--text-dim)', fontWeight: isWeekend || isFestivo ? 400 : 700 }}>
                  {d}
                </th>
              );
            })}
            <th style={{ ...TH, color: 'var(--amber)' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {employees.map(name => {
            const empData = byEmployee[name];
            let totalH = 0;
            return (
              <tr key={name} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ ...TD, fontWeight: 700, position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 1, whiteSpace: 'nowrap' }}>{name}</td>
                {days.map(d => {
                  const dow = new Date(año, mes, d).getDay();
                  const isWeekend = dow === 0 || dow === 6;
                  const iso = `${año}-${String(mes+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
                  const isFestivo = festivoSet.has(iso);
                  const f = empData[d];
                  if (!f) {
                    return (
                      <td key={d} style={{ ...TD, textAlign: 'center', color: '#4e5a7a' }}>
                        {isWeekend || isFestivo ? '' : '·'}
                      </td>
                    );
                  }
                  const h = parseFloat(f.horas_trabajadas) || 0;
                  totalH += h;
                  const std = jornada7hSet.has(iso) ? 7 : 8;
                  const color = h >= std ? '#4ade80' : h > 0 ? 'var(--amber)' : '#60a5fa';
                  return (
                    <td key={d} onClick={() => setSel(f)} style={{ ...TD, textAlign: 'center', color, fontWeight: 700, cursor: 'pointer' }}>
                      {h > 0 ? `${Math.floor(h)}:${String(Math.round((h%1)*60)).padStart(2,'0')}` : <span style={{ color: '#60a5fa', fontSize: 10 }}>E</span>}
                    </td>
                  );
                })}
                <td style={{ ...TD, color: 'var(--amber)', fontWeight: 800, whiteSpace: 'nowrap' }}>
                  {Math.floor(totalH)}h {String(Math.round((totalH%1)*60)).padStart(2,'0')}m
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ marginTop: 8, display: 'flex', gap: 14, fontSize: 10, color: 'var(--text-dim)' }}>
        <span><span style={{ color: '#4ade80', fontWeight: 700 }}>■</span> ≥8h</span>
        <span><span style={{ color: 'var(--amber)', fontWeight: 700 }}>■</span> {'<'}8h</span>
        <span><span style={{ color: '#60a5fa', fontWeight: 700 }}>E</span> Sin salida</span>
        <span><span style={{ color: '#4e5a7a' }}>·</span> Sin fichar · Clic en celda para detalles</span>
      </div>

      {sel && (
        <div style={{ position: 'fixed', inset: 0, background: '#000a', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 600 }}
          onClick={e => e.target === e.currentTarget && setSel(null)}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, width: '90%', maxWidth: 340 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)' }}>{sel.user_nombre}</div>
                <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{sel.fecha?.slice(0, 10)}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={() => { onEdit(sel); setSel(null); }}
                  style={{ background: 'rgba(245,158,11,.12)', border: '1px solid rgba(245,158,11,.3)', color: 'var(--amber)', padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                  ✏ Editar
                </button>
                <button onClick={() => setSel(null)} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
              </div>
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ background: 'var(--surface-deep)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: '#4ade80', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Entrada</div>
                <div style={{ fontSize: 16, fontWeight: 900, color: '#4ade80' }}>
                  {sel.entrada ? new Date(sel.entrada).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                </div>
                {sel.lat_entrada && (
                  <a href={mapsUrl(sel.lat_entrada, sel.lng_entrada)} target="_blank" rel="noreferrer"
                    style={{ ...LOC_LINK, display: 'inline-block', marginTop: 6, fontSize: 11 }}>
                    📍 {parseFloat(sel.lat_entrada).toFixed(5)}, {parseFloat(sel.lng_entrada).toFixed(5)}
                  </a>
                )}
                {!sel.lat_entrada && <div style={{ fontSize: 11, color: '#4e5a7a', marginTop: 4 }}>Sin ubicación</div>}
              </div>
              <div style={{ background: 'var(--surface-deep)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: '#f87171', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Salida</div>
                <div style={{ fontSize: 16, fontWeight: 900, color: sel.salida ? '#f87171' : 'var(--text-dim)' }}>
                  {sel.salida ? new Date(sel.salida).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                </div>
                {sel.lat_salida && (
                  <a href={mapsUrl(sel.lat_salida, sel.lng_salida)} target="_blank" rel="noreferrer"
                    style={{ ...LOC_LINK, display: 'inline-block', marginTop: 6, fontSize: 11 }}>
                    📍 {parseFloat(sel.lat_salida).toFixed(5)}, {parseFloat(sel.lng_salida).toFixed(5)}
                  </a>
                )}
                {!sel.lat_salida && <div style={{ fontSize: 11, color: '#4e5a7a', marginTop: 4 }}>Sin ubicación</div>}
              </div>
              {sel.horas_trabajadas && (
                <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 800, color: 'var(--amber)' }}>
                  {Math.floor(sel.horas_trabajadas)}h {String(Math.round((sel.horas_trabajadas%1)*60)).padStart(2,'0')}m trabajadas
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FichajeTable({ rows, onEdit }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ color: 'var(--text-dim)', fontSize: 10, textTransform: 'uppercase' }}>
            <th style={TH}>Empleado</th><th style={TH}>Entrada</th><th style={TH}>Salida</th><th style={TH}>Horas</th><th style={TH}>Ubicación</th><th style={TH}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(f => (
            <tr key={f.id} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ ...TD, fontWeight: 700 }}>{f.user_nombre}</td>
              <td style={{ ...TD, color: '#4ade80', fontWeight: 700 }}>{f.entrada ? new Date(f.entrada).toLocaleTimeString('es-ES',{hour:'2-digit',minute:'2-digit'}) : '—'}</td>
              <td style={{ ...TD, color: f.salida ? '#f87171' : 'var(--text-dim)', fontWeight: 700 }}>{f.salida ? new Date(f.salida).toLocaleTimeString('es-ES',{hour:'2-digit',minute:'2-digit'}) : '—'}</td>
              <td style={{ ...TD, color: 'var(--amber)', fontWeight: 700 }}>
                {f.salida && f.horas_trabajadas
                  ? `${Math.floor(f.horas_trabajadas)}h ${String(Math.round((f.horas_trabajadas%1)*60)).padStart(2,'0')}m`
                  : f.entrada ? <span style={{ color: '#60a5fa' }}>En curso</span> : '—'}
              </td>
              <td style={TD}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {f.lat_entrada
                    ? <a href={mapsUrl(f.lat_entrada, f.lng_entrada)} target="_blank" rel="noreferrer" style={LOC_LINK} title={`Entrada: ${parseFloat(f.lat_entrada).toFixed(5)}, ${parseFloat(f.lng_entrada).toFixed(5)}`}>📍E</a>
                    : <span style={{ color: '#4e5a7a', fontSize: 10 }}>—</span>}
                  {f.lat_salida
                    ? <a href={mapsUrl(f.lat_salida, f.lng_salida)} target="_blank" rel="noreferrer" style={LOC_LINK} title={`Salida: ${parseFloat(f.lat_salida).toFixed(5)}, ${parseFloat(f.lng_salida).toFixed(5)}`}>📍S</a>
                    : null}
                </div>
              </td>
              <td style={TD}>
                <button onClick={() => onEdit(f)}
                  style={{ background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.3)', color: 'var(--amber)', padding: '3px 8px', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                  ✏
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Dot({ color }) {
  return <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />;
}
function DotSquare({ color }) {
  return <span style={{ width: 7, height: 7, borderRadius: 2, background: color, display: 'inline-block', flexShrink: 0 }} />;
}
function VacInitials({ label, estado, tipo, title }) {
  const color = tipo === 'baja' ? '#f87171' : estado === 'aprobada' ? '#4ade80' : '#60a5fa';
  return (
    <span title={title} style={{
      fontSize: 7, fontWeight: 800, lineHeight: 1, padding: '2px 3px', borderRadius: 3,
      background: `${color}26`, color, border: `1px solid ${color}66`, flexShrink: 0,
    }}>{label}</span>
  );
}
function StatBox({ label, val, color }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 26, fontWeight: 900, color }}>{val}</div>
      <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 3 }}>{label}</div>
    </div>
  );
}
function EstadoBadge({ estado, tipo }) {
  if (tipo === 'baja') {
    return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'rgba(248,113,113,.12)', color: '#f87171', border: '1px solid rgba(248,113,113,.4)', whiteSpace: 'nowrap' }}>baja</span>;
  }
  const cfg = {
    aprobada:  { bg:'rgba(74,222,128,.12)',  color:'#4ade80', border:'rgba(74,222,128,.4)' },
    rechazada: { bg:'rgba(248,113,113,.12)', color:'#f87171', border:'rgba(248,113,113,.4)' },
    pendiente: { bg:'rgba(96,165,250,.1)',   color:'#60a5fa', border:'rgba(96,165,250,.4)' },
  }[estado] || { bg:'var(--surface2)', color:'var(--text-dim)', border:'var(--border)' };
  return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, whiteSpace: 'nowrap' }}>{estado}</span>;
}
function Overlay({ children, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000a', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 22, width: '90%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto' }}>
        {children}
      </div>
    </div>
  );
}
function Fld({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

const INP     = { background: 'var(--surface-deep)', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '8px 10px', width: '100%', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
const STITLE  = { fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 12 };
const ROW     = { background: 'var(--surface-deep)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' };
const BTN_NAV = { background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const BTN_OK  = { background: 'rgba(74,222,128,.12)', border: '1px solid rgba(74,222,128,.4)', color: '#4ade80', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 12 };
const BTN_DEL = { background: 'rgba(248,113,113,.12)', border: '1px solid rgba(248,113,113,.4)', color: '#f87171', padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 };
const BTN_SEC = { background: 'var(--surface2)', color: 'var(--text-dim)', border: 'none', padding: '9px 14px', borderRadius: 7, cursor: 'pointer', fontSize: 12 };
const BTN_IN  = { background: 'rgba(74,222,128,.12)', border: '1.5px solid rgba(74,222,128,.4)', color: '#4ade80', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 14 };
const BTN_OUT = { background: 'rgba(248,113,113,.12)', border: '1.5px solid rgba(248,113,113,.4)', color: '#f87171', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 14 };
const LOC_LINK = { color: '#38bdf8', textDecoration: 'none', fontWeight: 600, borderBottom: '1px dashed #38bdf844', cursor: 'pointer' };
const TH = { textAlign: 'left', padding: '4px 8px', fontWeight: 700 };
const TD = { padding: '7px 8px', color: 'var(--text-dim)' };

// Exports puramente aditivos para que Fichajes.js y Vacaciones.js reutilicen
// estos mismos componentes/estilos en vez de duplicarlos — no cambian en nada
// el comportamiento de Calendario (export default), que sigue igual.
export {
  MESES, isoDate, fmtFecha, fmtTime, fmtHoras, calcHorasVivas, mapsUrl,
  TIPOS_NO_LABORAL, JORNADA_STD,
  TimeBox, MonthCalendarCard, FichajeMonthGrid, FichajeTable, EstadoBadge, Overlay, Fld,
  INP, STITLE, ROW, BTN_NAV, BTN_OK, BTN_DEL, BTN_SEC, BTN_IN, BTN_OUT, LOC_LINK, TH, TD,
};
